import { useCallback, useEffect, useState } from "react"
import toast from "react-hot-toast"
import { updateUserProfile } from "@/app/actions"
import { Profile } from "@/models/profile"

type EditProfileProps = {
  description: string
  links: Profile["links"]
}

export const useEditProfile = ({ description, links }: EditProfileProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [newDescription, setNewDescription] = useState(description)
  const [linkError, setLinkError] = useState({ link: "", error: "" })

  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "auto"
    }
  }, [isDrawerOpen])

  const descriptionIsWithinMaxLength = newDescription.length <= 128

  const socialUsernameIsValid = (username: string) => {
    const usernameRegex = /^[a-zA-Z0-9_]{1,32}$/
    if (!usernameRegex.test(username)) return false
    if (username.length > 32) return false

    return true
  }

  const handleSave = useCallback(async (data: FormData) => {
    setIsSaving(true)
    if (!descriptionIsWithinMaxLength) return
    const links = {
      website: data.get("website") as string,
      youtube: data.get("youtube") as string,
      facebook: data.get("facebook") as string,
      tiktok: data.get("tiktok") as string,
      twitter: data.get("twitter") as string,
      instagram: data.get("instagram") as string,
      twitch: data.get("twitch") as string,
    }

    for (const [key, value] of Object.entries(links)) {
      if (key === "website") continue
      if (value && !socialUsernameIsValid(value)) {
        setLinkError({ link: key, error: "Invalid username" })
        setIsSaving(false)
        return
      }
    }

    try {
      await updateUserProfile(data)
      setIsDrawerOpen(false)
      toast.success("Profile updated.")
    } catch (error) {
      console.log(error)
      toast.error("Something went wrong.")
    } finally {
      setIsSaving(false)
    }
  }, [])

  const isDisabled = isSaving || !descriptionIsWithinMaxLength

  return {
    isDrawerOpen,
    setIsDrawerOpen,
    isSaving,
    newDescription,
    setNewDescription,
    linkError,
    descriptionIsWithinMaxLength,
    socialUsernameIsValid,
    handleSave,
    isDisabled,
  }
}
